// resources/js/Pages/Formula/questions/QuestionList.js

import React from 'react';
import { Card, Button, Alert, Badge } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { PRESET_QUESTIONS } from './questionUtils';

export default function QuestionList({ 
  questions = [], 
  onEdit,
  onDelete,
  onReorder,
  showDeleteConfirm,
  onConfirmDelete,
  onCancelDelete
}) {
  // Handle drag end
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const startIndex = result.source.index;
    const endIndex = result.destination.index;
    
    if (startIndex !== endIndex) {
      onReorder?.(startIndex, endIndex);
    }
  };

  // Get preset info for a question
  const getPresetInfo = (question) => {
    if (!question.data) return null;
    return PRESET_QUESTIONS.find(preset => preset.data === question.data);
  };

  // Get display info for question type
  const getTypeDisplay = (question) => {
    const preset = getPresetInfo(question);
    if (preset) {
      return {
        label: preset.label,
        icon: preset.icon,
        variant: 'primary',
        description: 'System dropdown'
      };
    }
    
    // NEW: Handle custom dropdown
    if (question.type === 'custom_dropdown') {
      const optionCount = question.options ? question.options.length : 0;
      return {
        label: 'Custom Dropdown',
        icon: '📋',
        variant: 'info',
        description: `${optionCount} custom options`
      };
    }
    
    return {
      label: 'Custom',
      icon: '✏️',
      variant: 'secondary', 
      description: 'Text input'
    };
  };

  if (questions.length === 0) {
    return (
      <div className="p-4 text-center text-muted">
        <div className="mb-2">📝</div>
        <div>No questions added yet.</div>
        <small>Click "Add Question" to create variables for your prompt.</small>
      </div>
    );
  }

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="questions">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {questions.map((question, index) => {
                const typeInfo = getTypeDisplay(question);
                
                return (
                  <Draggable 
                    key={question.id} 
                    draggableId={question.id.toString()} 
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`border-bottom p-3 ${
                          snapshot.isDragging ? 'shadow bg-light' : ''
                        }`}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          {/* Drag Handle */}
                          <div 
                            {...provided.dragHandleProps}
                            className="me-2 text-muted cursor-grab drag-handle"
                            style={{ paddingTop: '2px' }}
                            title="Drag to reorder"
                          >
                            ⋮⋮
                          </div>
                          
                          {/* Question Content */}
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2">
                              <span className="me-2">{typeInfo.icon}</span>
                              <span className="fw-medium">{question.label}</span>
                              <Badge 
                                bg={typeInfo.variant} 
                                className="ms-2"
                                title={typeInfo.description}
                              >
                                {typeInfo.label}
                              </Badge>
                            </div>
                            
                            <div className="text-muted small">
                              Variable: <code className="variable-code">{`{{${question.variable_name}}}`}</code>
                              {question.required && (
                                <span className="text-danger ms-2">• Required</span>
                              )}
                            </div>
                            
                            {/* NEW: Show custom dropdown options */}
                            {question.type === 'custom_dropdown' && question.options && (
                              <div className="text-muted small mt-1">
                                📋 Options: {question.options.map(opt => opt.label).join(', ')}
                              </div>
                            )}
                            
                            {question.help_text && (
                              <div className="text-muted small mt-1">
                                💡 {question.help_text}
                              </div>
                            )}
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="ms-3 d-flex gap-1">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => onEdit?.(question, index)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => onDelete?.(index)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Delete Confirmation */}
      {showDeleteConfirm !== null && (
        <Alert variant="danger" className="mt-3">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>Delete Question?</strong>
              <div className="small">
                This will permanently delete "{questions[showDeleteConfirm]?.label}"
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button size="sm" variant="outline-secondary" onClick={onCancelDelete}>
                Cancel
              </Button>
              <Button size="sm" variant="danger" onClick={onConfirmDelete}>
                Delete
              </Button>
            </div>
          </div>
        </Alert>
      )}
    </>
  );
}